import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Eye, Send, Search } from "lucide-react";
import { format, parseISO } from "date-fns";
import { es } from "date-fns/locale";
import { adminHttp, formatApiError } from "../../lib/adminApi";
import { toast } from "sonner";
import LogModal from "../components/EmailLogModal";

const TEMPLATES = [
  "quote_received", "catalog_delivery", "case_studies_delivery",
  "preliminary_quote", "nurturing_day_1", "nurturing_day_3", "nurturing_day_7",
  "internal_lead_alert", "post_quote_followup", "subscribe_thanks",
];

export default function EmailsLogs() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [filters, setFilters] = useState({ template_key: "", status: "", lead_id: "" });
  const [loading, setLoading] = useState(true);
  const [openId, setOpenId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const params = { page, limit: 25 };
      if (filters.template_key) params.template_key = filters.template_key;
      if (filters.status) params.status = filters.status;
      if (filters.lead_id) params.lead_id = filters.lead_id;
      const { data } = await adminHttp.get("/admin/emails/logs", { params });
      setItems(data.items || []);
      setTotal(data.total || 0);
      setPages(data.pages || 1);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [page, filters.template_key, filters.status, filters.lead_id]);

  const resend = async (id) => {
    try {
      await adminHttp.post(`/admin/emails/resend/${id}`);
      toast.success("Email reenviado");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="p-6 lg:p-10" data-testid="emails-logs-page">
      <div className="flex items-center gap-3 mb-2">
        <span className="divider-accent" /><span className="kicker">Logs</span>
      </div>
      <h1 className="font-display font-black tracking-tightest text-4xl md:text-5xl">Historial de emails</h1>
      <p className="mt-3 text-white/55 max-w-2xl">Cada envío queda registrado con su status, errores y HTML rendereado.</p>

      {/* Filters */}
      <div className="mt-8 grid grid-cols-1 md:grid-cols-4 gap-3">
        <select
          value={filters.template_key}
          onChange={(e) => { setFilters({ ...filters, template_key: e.target.value }); setPage(1); }}
          className="bg-white border border-[#DDE2D5] px-4 py-3 text-[#1F2937] text-sm focus:outline-none focus:border-accent"
          data-testid="filter-template"
        >
          <option value="">Todas las plantillas</option>
          {TEMPLATES.map((t) => <option key={t} value={t}>{t}</option>)}
        </select>
        <select
          value={filters.status}
          onChange={(e) => { setFilters({ ...filters, status: e.target.value }); setPage(1); }}
          className="bg-white border border-[#DDE2D5] px-4 py-3 text-[#1F2937] text-sm focus:outline-none focus:border-accent"
          data-testid="filter-status"
        >
          <option value="">Todos los status</option>
          <option value="sent">Enviados</option>
          <option value="failed">Fallidos</option>
          <option value="queued">En cola</option>
          <option value="skipped">Omitidos</option>
        </select>
        <div className="relative md:col-span-2">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#6B7280]" />
          <input
            placeholder="Filtrar por lead_id…"
            value={filters.lead_id}
            onChange={(e) => setFilters({ ...filters, lead_id: e.target.value })}
            onKeyDown={(e) => { if (e.key === "Enter") { setPage(1); load(); } }}
            className="w-full bg-white border border-[#DDE2D5] pl-10 pr-4 py-3 text-[#1F2937] text-sm focus:outline-none focus:border-accent"
            data-testid="filter-lead"
          />
        </div>
      </div>

      <div className="mt-6 bg-white border border-[#DDE2D5] overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-[#F2F4ED] text-[#6B7280] font-mono text-[10px] uppercase tracking-widest">
              <th className="text-left px-6 py-3">Fecha</th>
              <th className="text-left px-6 py-3">Plantilla</th>
              <th className="text-left px-6 py-3">Destinatario</th>
              <th className="text-left px-6 py-3">Subject</th>
              <th className="text-left px-6 py-3">Status</th>
              <th className="text-left px-6 py-3">Lead</th>
              <th className="text-right px-6 py-3">Acciones</th>
            </tr>
          </thead>
          <tbody data-testid="emails-logs-table">
            {loading ? (
              <tr><td colSpan={7} className="px-6 py-10 text-center text-[#6B7280] font-mono text-xs">Cargando…</td></tr>
            ) : items.map((l) => (
              <tr key={l.id} className="border-t border-[#EAECEA] hover:bg-[#F2F4ED]/50">
                <td className="px-6 py-3 font-mono text-[11px] text-[#374151] whitespace-nowrap">
                  {format(parseISO(l.created_at), "d MMM · HH:mm", { locale: es })}
                </td>
                <td className="px-6 py-3 font-mono text-[11px] text-accent whitespace-nowrap">{l.template_key}</td>
                <td className="px-6 py-3 text-white/75 truncate max-w-[220px]">{(l.to || []).join(", ")}</td>
                <td className="px-6 py-3 text-[#374151] truncate max-w-[300px]">{l.subject}</td>
                <td className="px-6 py-3">
                  <StatusBadge status={l.status} />
                  {l.error && <div className="text-red-400 text-[10px] mt-1 truncate max-w-[200px]">{l.error}</div>}
                </td>
                <td className="px-6 py-3">
                  {l.lead_id ? (
                    <Link to={`/admin/leads/${l.lead_id}`} className="text-accent font-mono text-[11px] hover:underline">
                      {l.lead_id.slice(0, 8)}
                    </Link>
                  ) : <span className="text-[#9CA3AF]">—</span>}
                </td>
                <td className="px-6 py-3 text-right whitespace-nowrap">
                  <button onClick={() => setOpenId(l.id)} className="p-2 text-[#4B5563] hover:text-accent" title="Ver">
                    <Eye className="w-4 h-4" />
                  </button>
                  <button onClick={() => resend(l.id)} className="p-2 text-[#4B5563] hover:text-accent" title="Reenviar">
                    <Send className="w-4 h-4" />
                  </button>
                </td>
              </tr>
            ))}
            {!loading && items.length === 0 && (
              <tr><td colSpan={7} className="px-6 py-10 text-center text-[#6B7280] font-mono text-xs">Sin emails que coincidan con el filtro.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="mt-4 flex items-center justify-between">
        <p className="font-mono text-[11px] uppercase tracking-widest text-[#6B7280]">Total: {total} · Página {page}/{pages}</p>
        <div className="flex gap-2">
          <button disabled={page <= 1} onClick={() => setPage(page - 1)}
            className="px-4 py-2 border border-[#DDE2D5] text-[#374151] text-xs font-mono uppercase disabled:opacity-40 hover:border-accent">Anterior</button>
          <button disabled={page >= pages} onClick={() => setPage(page + 1)}
            className="px-4 py-2 border border-[#DDE2D5] text-[#374151] text-xs font-mono uppercase disabled:opacity-40 hover:border-accent">Siguiente</button>
        </div>
      </div>

      {openId && <LogModal logId={openId} onClose={() => setOpenId(null)} onResend={() => { resend(openId); setOpenId(null); }} />}
    </div>
  );
}

function StatusBadge({ status }) {
  const map = {
    sent: { bg: "#1e4d29", text: "#7fff9f" }, failed: { bg: "#4d1e1e", text: "#ff7f7f" },
    queued: { bg: "#1a1a2e", text: "#8a8aff" }, skipped: { bg: "#2e2e2e", text: "#aaa" },
  };
  const c = map[status] || { bg: "#2e2e2e", text: "#aaa" };
  return <span style={{ background: c.bg, color: c.text }} className="px-2 py-1 text-[10px] font-mono uppercase tracking-wider">{status}</span>;
}
